import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site-shell";
import {
  ShieldCheck,
  HeartHandshake,
  Eye,
  FileBadge2,
  Compass,
  Target,
  ArrowRight,
  ClipboardCheck,
  FileCheck2,
  PhoneCall,
  BadgeCheck,
  CheckCircle2,
} from "lucide-react";
import {
  initiatives,
  foundationMission,
  foundationVision,
  leaders,
  manavtaLogo,
} from "@/data/sample";

const logoUrl = manavtaLogo;

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — Manavta" },
      {
        name: "description",
        content:
          "Manavta brings together associations, communities, sanghs, clubs, animal shelters and non-profits with people who want to help.",
      },
      { property: "og:title", content: "About — Manavta" },
      {
        property: "og:description",
        content: "Our mission, vision and board — Manavta.",
      },
    ],
  }),
  component: About,
});

const pillars = [
  {
    icon: ShieldCheck,
    title: "Verification first",
    body: "Every applicant and every fund is verified before money moves.",
  },
  {
    icon: FileBadge2,
    title: "Instant 80G receipts",
    body: "The moment you donate, your 80G tax-exempt receipt is delivered to your inbox and phone.",
  },
  {
    icon: HeartHandshake,
    title: "Donor-first",
    body: "Choose your cause, follow its journey and see how your gift changes lives.",
  },
];

const verificationSteps = [
  {
    icon: ClipboardCheck,
    title: "Documents submitted",
    body: "Institution submits registration, PAN, bank and address documents.",
  },
  {
    icon: FileCheck2,
    title: "Tax eligibility checked",
    body: "80G and 12A certificates checked for tax-benefit eligibility.",
  },
  {
    icon: PhoneCall,
    title: "Phone verification",
    body: "Admin team performs phone verification with the contact person.",
  },
  {
    icon: BadgeCheck,
    title: "Profile approved",
    body: "Profile is approved; institution can start creating causes.",
  },
  { icon: Eye, title: "Cause reviewed", body: "Each cause is reviewed before going public." },
];

const trackingHighlights = [
  "Every donation is attached to a specific, named cause",
  "Institutions post photo & expense updates as work progresses",
  "Donors get notified the moment an update is posted",
  "Full PDF trail downloadable any time from your dashboard",
];

function About() {
  return (
    <SiteShell>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(135deg, #fffbf5 0%, #fff8ee 35%, #fef9f0 60%, #f8f4ff 100%)",
          }}
        />
        {/* Decorative Background Orbs */}
        <div
          className="absolute top-[-80px] right-[-80px] -z-10 h-[520px] w-[520px] rounded-full opacity-30"
          style={{
            background: "radial-gradient(circle, #f59e0b 0%, #fbbf24 40%, transparent 70%)",
          }}
        />
        <div
          className="absolute bottom-[-120px] left-[-100px] -z-10 h-[480px] w-[480px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #6366f1 0%, #818cf8 50%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[40%] left-[45%] -z-10 h-[300px] w-[300px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #10b981 0%, #34d399 50%, transparent 70%)",
          }}
        />
        {/* Subtle dot-grid texture */}
        <div
          className="absolute inset-0 -z-10 opacity-[0.035]"
          style={{
            backgroundImage: "radial-gradient(circle, #4338ca 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:py-24">
          <div className="mx-auto inline-flex items-center gap-3 rounded-full border border-[var(--maroon)]/15 bg-white/90 py-1 pl-1 pr-3 text-xs font-medium text-[var(--maroon-deep)] shadow-[var(--shadow-soft)]">
            <img src={logoUrl} alt="" className="h-6 w-auto object-contain" />
            Registered organisation · 80G & 12A certified
          </div>
          <h1 className="mx-auto mt-5 max-w-3xl font-display text-3xl font-semibold leading-tight text-[var(--maroon-deep)] sm:text-5xl lg:text-6xl">
            About Manavta
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-foreground/75 sm:text-lg">
            Empowering Everyone to Give, Anytime, Anywhere, in Any Amount.
          </p>
        </div>
      </section>

      {/* ── Mission & Vision ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
            Our purpose
          </p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">
            Why we exist
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl border border-[var(--maroon)]/10 bg-white p-8 shadow-[var(--shadow-soft)]">
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[var(--maroon)] to-[var(--saffron)]" />
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[var(--maroon)]/8 text-[var(--maroon)]">
              <Target className="h-6 w-6" />
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
              Our mission
            </p>
            <p className="mt-3 text-foreground/80 leading-relaxed">{foundationMission.statement}</p>
            <ul className="mt-5 space-y-2.5 text-sm text-foreground/75">
              {foundationMission.pillars.map((p) => (
                <li key={p} className="flex gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--saffron-deep)]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative overflow-hidden rounded-3xl border border-[var(--maroon)]/10 bg-white p-8 shadow-[var(--shadow-soft)]">
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[var(--saffron)] to-[var(--maroon)]" />
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[var(--saffron)]/12 text-[var(--saffron-deep)]">
              <Compass className="h-6 w-6" />
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
              Our vision
            </p>
            <p className="mt-3 text-foreground/80 leading-relaxed">{foundationVision.statement}</p>
            <ul className="mt-5 space-y-2.5 text-sm text-foreground/75">
              {foundationVision.pillars.map((p) => (
                <li key={p} className="flex gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--maroon)]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── How we work ───────────────────────────────────────────────── */}
      <section className="bg-[var(--cream-2)] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
            How we work
          </p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">
            Trusted. Transparent.
          </h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {pillars.map((p) => (
              <div
                key={p.title}
                className="group rounded-[2.5rem] border border-[var(--maroon)]/10 bg-gradient-to-b from-[var(--saffron)]/16 via-white to-[var(--cream-2)] px-5 py-10 text-center shadow-[var(--shadow-soft)] transition duration-300 hover:-translate-y-2 hover:scale-[1.03] hover:border-[var(--maroon)]/30 hover:shadow-[var(--shadow-card)]"
              >
                <p.icon
                  className="mx-auto h-9 w-9 text-[var(--maroon)] transition duration-300 group-hover:scale-110 group-hover:text-[var(--saffron-deep)]"
                  strokeWidth={1.5}
                />
                <h3 className="mt-5 font-display text-base font-bold text-[var(--maroon-deep)] lg:text-lg">
                  {p.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground/65">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Leadership teaser ────────────────────────────────────────── */}
      <section className="bg-[var(--cream-2)] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-[var(--maroon)]/10 bg-white p-8 shadow-[var(--shadow-soft)] sm:p-10 md:flex-row md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">
                Board & Advisory
              </p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)]">
                Team members guiding the foundation
              </h2>
              <p className="mt-2 max-w-lg text-foreground/70">
                Meet the full board and advisory council on the leadership page.
              </p>
              <Link
                to="/leadership"
                className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)] transition hover:bg-[var(--maroon-deep)]"
              >
                See leadership <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex shrink-0 -space-x-4">
              {leaders.map((l) => (
                <div
                  key={l.name}
                  className="h-16 w-16 overflow-hidden rounded-full ring-4 ring-white shadow-[var(--shadow-soft)]"
                  title={l.name}
                >
                  {l.photo ? (
                    <img
                      src={l.photo}
                      alt={l.name}
                      className="h-full w-full object-cover object-[50%_15%]"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-[var(--maroon)] font-display text-sm font-bold text-white">
                      {l.initials}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Verification process & donation tracking ─────────────────── */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <h2 className="font-display text-3xl font-semibold text-[var(--maroon-deep)]">
                Our verification process
              </h2>
              <ol className="mt-7 relative border-l-2 border-[var(--saffron)]/30 pl-7">
                {verificationSteps.map((s, idx) => (
                  <li key={s.title} className="relative pb-8 last:pb-0">
                    <span className="absolute -left-[39px] top-0 grid h-8 w-8 place-items-center rounded-full bg-[var(--maroon)] text-white shadow-[var(--shadow-soft)]">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <p className="text-xs font-bold uppercase tracking-wider text-[var(--saffron-deep)]">
                      Step {idx + 1}
                    </p>
                    <h3 className="mt-0.5 font-display text-base font-semibold text-[var(--maroon-deep)]">
                      {s.title}
                    </h3>
                    <p className="mt-1 text-sm text-foreground/70">{s.body}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl border border-[var(--maroon)]/10 bg-[var(--cream-2)] p-8">
              <h2 className="font-display text-3xl font-semibold text-[var(--maroon-deep)]">
                How donations are tracked
              </h2>
              <p className="mt-4 text-foreground/80 leading-relaxed">
                Every rupee stays visible from the moment it's given to the moment it's spent.
              </p>
              <ul className="mt-6 space-y-3.5">
                {trackingHighlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-foreground/80">
                    <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[var(--saffron-deep)]" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/initiatives"
                  className="rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)] transition hover:bg-[var(--maroon-deep)]"
                >
                  Explore initiatives ({initiatives.length})
                </Link>
                <Link
                  to="/leadership"
                  className="rounded-full border border-[var(--maroon)]/20 bg-white px-5 py-2.5 text-sm font-semibold text-[var(--maroon)] transition hover:bg-[var(--cream-2)]"
                >
                  Meet our leadership
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────────────── */}
      <section className="px-4 pb-16 sm:px-6">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-3xl border border-[var(--saffron-deep)]/20 bg-gradient-to-br from-[var(--saffron)]/12 via-white to-[var(--cream-2)] px-8 py-14 text-center shadow-[var(--shadow-card)] sm:px-16">
          <h2 className="font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">
            Ready to give with confidence?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-foreground/70">
            Join thousands of donors who trust Manavta for verified causes, instant 80G receipts and
            complete transparency on every rupee given.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/donate"
              className="rounded-full bg-[var(--maroon)] px-6 py-3 text-sm font-semibold text-[var(--cream)] shadow-sm transition hover:bg-[var(--maroon-deep)] active:scale-95"
            >
              Donate Now
            </Link>
            <Link
              to="/initiatives"
              className="rounded-full border border-[var(--maroon)]/20 bg-white px-6 py-3 text-sm font-semibold text-[var(--maroon)] transition hover:bg-[var(--cream-2)]"
            >
              Explore causes
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
